import React, { useState } from 'react';
import { PublisherMetrics } from '../../api';
import { PublisherMetricsModal } from './PublisherMetricsModal';

/**
 * Compact mini-chart visualising an analyst's "earner score" across all 10
 * timeframes in the order [1h, 1d, 1w, 2w, 4w, 2m, 3m, 4m, 5m, 6m].
 *
 * Reading the chart:
 *   - Dashed line across middle = 0% reference
 *   - Bar above the line        = positive stance-weighted return (green)
 *   - Bar below the line        = negative stance-weighted return (red)
 *   - Bar height/opacity        = magnitude, capped at +/- PCT_CAP%
 *   - Low-n (sampleCount < 2)   = bar opacity halved, so a 1-sample bar
 *                                  can't be mistaken for a well-sampled one
 *   - Missing score / no data   = no bar at that column (visible gap)
 *
 * Hover any bar to see "{tf}: {+/-X.X}% (n={samples})" via the SVG <title>.
 *
 * Click anywhere on the chart (or activate via keyboard) to open a detail
 * modal listing every timeframe with sample count + confidence flag.
 *
 * Empty-state rendering matches PriceSnapshotMiniChart: when no score has
 * been backfilled yet, the chart draws the reference line plus an inline
 * "awaiting backfill" caption rather than rendering as an empty SVG.
 */
export interface PublisherMetricsMiniChartProps {
    metrics?: PublisherMetrics | null;
    /** Shown in the modal header. */
    authorName?: string;
    /** Total width in px, default 280. Height is fixed at 40. */
    width?: number;
}

interface Cell {
    label: string;
    score: number | undefined | null;
    sampleCount: number | undefined | null;
}

function cellsForMetrics(m?: PublisherMetrics | null): Cell[] {
    return [
        { label: '1h', score: m?.score1h, sampleCount: m?.sampleCount1h },
        { label: '1d', score: m?.score1d, sampleCount: m?.sampleCount1d },
        { label: '1w', score: m?.score1w, sampleCount: m?.sampleCount1w },
        { label: '2w', score: m?.score2w, sampleCount: m?.sampleCount2w },
        { label: '4w', score: m?.score4w, sampleCount: m?.sampleCount4w },
        { label: '2m', score: m?.score2m, sampleCount: m?.sampleCount2m },
        { label: '3m', score: m?.score3m, sampleCount: m?.sampleCount3m },
        { label: '4m', score: m?.score4m, sampleCount: m?.sampleCount4m },
        { label: '5m', score: m?.score5m, sampleCount: m?.sampleCount5m },
        { label: '6m', score: m?.score6m, sampleCount: m?.sampleCount6m },
    ];
}

const HEIGHT = 40;
const PAD_X = 8;
const CENTER_Y = HEIGHT / 2;
/** % at which the bar height/opacity saturates. Matches the observed score distribution. */
const PCT_CAP = 5;
/** Max bar extent above/below the reference line, in px. */
const MAX_BAR = (HEIGHT / 2) - 4;

const GREEN = [34, 197, 94];   // #22c55e
const RED   = [239, 68, 68];   // #ef4444
const PLACEHOLDER_COLOR = '#9ca3af';
const PLACEHOLDER_TEXT  = 'awaiting backfill';

function fillForScore(score: number, lowN: boolean): string {
    const [r, g, b] = score >= 0 ? GREEN : RED;
    const mag = Math.min(Math.abs(score) / PCT_CAP, 1);
    // Match PriceSnapshotMiniChart: floor opacity at 0.30 so tiny moves stay perceptible.
    let opacity = 0.30 + 0.70 * mag;
    // Low-n muting: halve the effective alpha so a 1-sample bar visibly differs
    // from a well-sampled bar of the same magnitude.
    if (lowN) opacity *= 0.5;
    return `rgba(${r},${g},${b},${opacity.toFixed(2)})`;
}

export const PublisherMetricsMiniChart: React.FC<PublisherMetricsMiniChartProps> = ({
    metrics,
    authorName,
    width = 280,
}) => {
    const [open, setOpen] = useState(false);

    const cells = cellsForMetrics(metrics);

    // 10 evenly-spaced columns. We pad both sides so the leftmost and rightmost
    // bars don't kiss the edge of the SVG box.
    const usable = width - 2 * PAD_X;
    const step   = cells.length > 1 ? usable / (cells.length - 1) : 0;
    const colX   = (i: number) => PAD_X + i * step;

    const refY = CENTER_Y;
    const bars: React.ReactElement[] = [];
    let anyRendered = false;

    cells.forEach((cell, idx) => {
        const x = colX(idx);
        const hasScore = typeof cell.score === 'number' && Number.isFinite(cell.score);
        if (!hasScore) return;
        const score = cell.score as number;
        const samples = typeof cell.sampleCount === 'number' ? cell.sampleCount : 0;
        const lowN = samples < 2;
        const mag = Math.min(Math.abs(score), PCT_CAP);
        const h   = (mag / PCT_CAP) * MAX_BAR;
        const y   = score >= 0 ? refY - h : refY;
        anyRendered = true;
        bars.push(
            <rect
                key={cell.label}
                x={x - 2.5}
                y={y}
                width={5}
                height={Math.max(h, 1)}
                fill={fillForScore(score, lowN)}
                rx={1}
                data-testid={`pmc-bar-${cell.label}`}
                data-low-n={lowN ? 'true' : 'false'}
            >
                <title>
                    {`${cell.label}: ${score >= 0 ? '+' : ''}${score.toFixed(1)}% (n=${samples})`}
                </title>
            </rect>
        );
    });

    // Hover titles for "missing" columns — useful because the gap alone doesn't
    // tell the user which timeframe is empty. We render an invisible rect so the
    // tooltip surface exists but no visual bar is drawn.
    cells.forEach((cell, idx) => {
        const x = colX(idx);
        const hasScore = typeof cell.score === 'number' && Number.isFinite(cell.score);
        if (hasScore) return;
        bars.push(
            <rect
                key={`gap-${cell.label}`}
                x={x - 2.5}
                y={refY - MAX_BAR}
                width={5}
                height={MAX_BAR * 2}
                fill="transparent"
                data-testid={`pmc-gap-${cell.label}`}
            >
                <title>{`${cell.label} — no data yet`}</title>
            </rect>
        );
    });

    const showPlaceholder = !anyRendered;
    const ariaLabel = anyRendered
        ? `Earner scores by timeframe for ${authorName ?? 'analyst'}`
        : `Earner scores awaiting backfill for ${authorName ?? 'analyst'}`;

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label={`Open earner score details for ${authorName ?? 'analyst'}`}
                style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                    display: 'inline-block',
                    lineHeight: 0,
                }}
            >
                <svg
                    width={width}
                    height={HEIGHT}
                    viewBox={`0 0 ${width} ${HEIGHT}`}
                    style={{ display: 'block' }}
                    role="img"
                    aria-label={ariaLabel}
                >
                    {/* Dashed 0% reference line. */}
                    <line
                        x1={PAD_X - 4}
                        y1={refY}
                        x2={width - PAD_X + 4}
                        y2={refY}
                        stroke="#9ca3af"
                        strokeWidth={0.6}
                        strokeDasharray="2 3"
                        opacity={0.45}
                    />
                    {bars}
                    {showPlaceholder && (
                        <text
                            data-testid="publisher-metrics-placeholder"
                            x={width / 2}
                            y={refY}
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontSize={11}
                            fontFamily="monospace"
                            fill={PLACEHOLDER_COLOR}
                        >
                            {PLACEHOLDER_TEXT}
                        </text>
                    )}
                </svg>
            </button>
            {open && (
                <PublisherMetricsModal
                    metrics={metrics}
                    authorName={authorName}
                    visible={open}
                    onDismiss={() => setOpen(false)}
                />
            )}
        </>
    );
};
